import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { WhitelabelService } from '../whitelabel.service';
import {
  WHITE_LABEL_OVERRIDES,
  STORE_OVERRIDES,
} from './customer-ui-overrides';

@Injectable({
  providedIn: 'root',
})
export class CustomerUiTranslationOverrideService {
  private appliedKey: string | null = null;
  /** The white-label and store overrides of the last `apply()`, merged. */
  private appliedOverrides: Record<string, string> = {};

  constructor(
    private translate: TranslateService,
    private whitelabelService: WhitelabelService,
  ) {}

  apply(locale: string, storeId: number): void {
    const whiteLabelCode = this.whitelabelService.getWhiteLabelCode();
    const cacheKey = `${storeId}:${whiteLabelCode || ''}:${locale}`;

    if (this.appliedKey === cacheKey) {
      return;
    }

    const wl = whiteLabelCode
      ? WHITE_LABEL_OVERRIDES[whiteLabelCode]?.[locale] || {}
      : {};
    const store = STORE_OVERRIDES[String(storeId)]?.[locale] || {};
    const merged = { ...wl, ...store };
    this.appliedOverrides = merged;

    if (Object.keys(merged).length > 0) {
      this.translate.setTranslation(locale, this.expandKeys(merged), true);
    }

    this.appliedKey = cacheKey;
  }

  resetCache(): void {
    this.appliedKey = null;
    this.appliedOverrides = {};
  }

  /**
   * True when the overrides in force blank `key` on purpose (story #11854). The
   * translated text cannot tell: `CustomParser` treats a blank value as missing
   * and falls back to the English bundle, so a component that must hide a line
   * for such a store asks here. Blank uses the parser's own test.
   */
  isBlanked(key: string): boolean {
    const value = this.appliedOverrides[key];
    return typeof value === 'string' && value.replace(/\s|\u200B/g, '') === '';
  }

  private expandKeys(flat: Record<string, string>): Record<string, any> {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(flat)) {
      const parts = key.split('.');
      let current = result;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!current[parts[i]] || typeof current[parts[i]] !== 'object') {
          current[parts[i]] = {};
        }
        current = current[parts[i]];
      }
      current[parts[parts.length - 1]] = value;
    }
    return result;
  }
}
